/**
 * Shared validation and payload helpers for season create/update (standalone + campaign flows).
 */

export const CURRENCIES = ['CHF', 'EUR', 'USD'];

export function parseRegions(regions) {
  if (Array.isArray(regions)) return regions;
  if (typeof regions === 'string') {
    try {
      return JSON.parse(regions);
    } catch (_) {
      return [];
    }
  }
  return [];
}

/** Convert ISO or date string to datetime-local value (YYYY-MM-DDTHH:mm) in local time */
export function toLocalDatetimeInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${h}:${min}`;
}

export function validateSeasonDateOrder(form) {
  const sStart = new Date(form.start_date);
  const sEnd = new Date(form.end_date);
  if (Number.isNaN(sStart.getTime()) || Number.isNaN(sEnd.getTime())) return null;
  if (sEnd.getTime() < sStart.getTime()) {
    return 'Season end must be on or after season start.';
  }
  if (form.registration_start && form.registration_end) {
    const rS = new Date(form.registration_start);
    const rE = new Date(form.registration_end);
    if (!Number.isNaN(rS.getTime()) && !Number.isNaN(rE.getTime()) && rE.getTime() < rS.getTime()) {
      return 'Registration closes must be on or after registration opens.';
    }
  }
  return null;
}

export function toISOOrNull(datetimeLocal) {
  if (!datetimeLocal || !datetimeLocal.trim()) return null;
  const d = new Date(datetimeLocal);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

/** Same required checks as legacy standalone create */
export function validateSeasonFormBasics(form) {
  if (!form.name?.trim() || !form.start_date || !form.end_date) {
    return 'Please fill season name, start and end date & time.';
  }
  return validateSeasonDateOrder(form);
}

export function parseRegistrationFee(form) {
  const fee =
    form.registration_fee != null && form.registration_fee !== '' ? parseFloat(form.registration_fee) : NaN;
  if (typeof fee !== 'number' || isNaN(fee) || fee < 0) {
    return { error: 'Registration fee is required and must be a valid amount (e.g. 50).' };
  }
  return { fee };
}

export function validateSeasonCurrency(form) {
  if (!form.currency || !form.currency.trim()) return 'Currency is required.';
  return null;
}

/**
 * Body for POST /admin/seasons and fields accepted by POST .../event-tournaments/:id/seasons/create
 */
export function buildSeasonApiPayload(form) {
  const basics = validateSeasonFormBasics(form);
  if (basics) throw new Error(basics);
  const { error, fee } = parseRegistrationFee(form);
  if (error) throw new Error(error);
  const curErr = validateSeasonCurrency(form);
  if (curErr) throw new Error(curErr);

  const deadlineStr = typeof form.skill_level_edit_deadline === 'string' ? form.skill_level_edit_deadline.trim() : '';

  return {
    name: form.name.trim(),
    start_date: toISOOrNull(form.start_date) || form.start_date,
    end_date: toISOOrNull(form.end_date) || form.end_date,
    registration_start: toISOOrNull(form.registration_start) || toISOOrNull(form.start_date) || form.start_date,
    registration_end: toISOOrNull(form.registration_end) || toISOOrNull(form.end_date) || form.end_date,
    registration_fee: fee,
    currency: form.currency.trim(),
    regions_allowed: Array.isArray(form.regions_allowed) ? form.regions_allowed : [],
    score_submission_locking_enabled: form.score_submission_locking_enabled !== false,
    skill_level_edit_deadline: deadlineStr ? (toISOOrNull(form.skill_level_edit_deadline) || null) : null,
    lock_skill_level_when_registration_closed: form.lock_skill_level_when_registration_closed !== false,
  };
}

export function getDefaultSeasonFormStandalone() {
  return {
    name: '',
    start_date: '',
    end_date: '',
    registration_start: '',
    registration_end: '',
    registration_fee: '',
    currency: 'CHF',
    regions_allowed: [],
    score_submission_locking_enabled: true,
    skill_level_edit_deadline: '',
    lock_skill_level_when_registration_closed: true,
  };
}

/** Same fields as standalone, plus campaign-only activate flag */
export function getDefaultSeasonFormInCampaign() {
  return {
    ...getDefaultSeasonFormStandalone(),
    activate_now: true,
  };
}
